import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAdminCheckouts } from '../../services/admin/adminCheckoutApi';
import { fetchConferenceSubscriptionTracker } from '../../services/admin/subscriptionTrackerApi';
import { CONFERENCE_TRACKER_SPORTS } from '../../types/admin/subscriptionTracker';
import { AdminCheckoutUserGroup } from '../../types/adminCheckout';
import { getSportLabel } from '../../utils/admin/formatSubscriptionDate';
import Spinner from '../Spinner';
import CheckoutListTable from './CheckoutListTable';

const CONFERENCE_SPORT_LABELS: Record<string, string> = {
  MFB: "Men's Football",
  MBB: "Men's Basketball",
  WBB: "Women's Basketball",
  MIH: "Men's Ice Hockey",
  WIH: "Women's Ice Hockey",
  MSO: "Men's Soccer",
  WSO: "Women's Soccer",
  MVB: "Men's Volleyball",
  WVB: "Women's Volleyball",
};

function conferenceSportLabel(code: string): string {
  return CONFERENCE_SPORT_LABELS[code] ?? getSportLabel(code);
}

const SPORT_OPTIONS = CONFERENCE_TRACKER_SPORTS.map((code) => ({
  value: code,
  label: `${conferenceSportLabel(code)} (${code})`,
}));

const CHECKOUT_USER_PAGE_SIZE = 10;

const AdminCheckoutTab: React.FC = () => {
  const [listSearch, setListSearch] = useState('');
  const [debouncedListSearch, setDebouncedListSearch] = useState('');
  const [checkoutGroups, setCheckoutGroups] = useState<AdminCheckoutUserGroup[]>([]);
  const [listHasMore, setListHasMore] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [listLoadingMore, setListLoadingMore] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const listLoadingRef = useRef(false);
  const listOffsetRef = useRef(0);
  listLoadingRef.current = listLoading || listLoadingMore;

  const [filterSportCode, setFilterSportCode] = useState('');
  const [filterConferenceName, setFilterConferenceName] = useState('');
  const [conferenceOptions, setConferenceOptions] = useState<string[]>([]);
  const [conferencesLoading, setConferencesLoading] = useState(false);

  const [appliedListFilter, setAppliedListFilter] = useState<{
    sport_code?: string;
    conference_name?: string;
  }>({});

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedListSearch(listSearch);
      listOffsetRef.current = 0;
      setCheckoutGroups([]);
    }, 300);
    return () => clearTimeout(timer);
  }, [listSearch]);

  const prevFilterSportRef = useRef(filterSportCode);
  useEffect(() => {
    const sportChanged = prevFilterSportRef.current !== filterSportCode;
    prevFilterSportRef.current = filterSportCode;
    if (sportChanged) {
      setFilterConferenceName('');
    }

    const controller = new AbortController();
    setConferencesLoading(true);

    const sportsToLoad = filterSportCode ? [filterSportCode] : [...CONFERENCE_TRACKER_SPORTS];

    (async () => {
      const names = new Set<string>();
      for (const code of sportsToLoad) {
        if (controller.signal.aborted) return;
        const result = await fetchConferenceSubscriptionTracker(
          { sport_code: code, limit: 200, offset: 0 },
          controller.signal
        );
        if (controller.signal.aborted || result.aborted) return;
        for (const c of result.data?.conferences ?? []) {
          if (c.conference_name) names.add(c.conference_name);
        }
      }
      setConferenceOptions([...names].sort((a, b) => a.localeCompare(b)));
      setConferencesLoading(false);
    })();

    return () => controller.abort();
  }, [filterSportCode]);

  const loadCheckoutList = useCallback(async (options?: {
    offset?: number;
    search?: string;
    sport_code?: string;
    conference_name?: string;
    append?: boolean;
  }) => {
    const append = options?.append ?? false;
    const nextOffset = options?.offset ?? (append ? listOffsetRef.current : 0);
    const nextSearch = options?.search ?? debouncedListSearch;
    const nextSport = options?.sport_code ?? appliedListFilter.sport_code;
    const nextConference = options?.conference_name ?? appliedListFilter.conference_name;

    if (append) {
      setListLoadingMore(true);
    } else {
      setListLoading(true);
      setListError(null);
    }

    const result = await fetchAdminCheckouts({
      search: nextSearch || undefined,
      limit: CHECKOUT_USER_PAGE_SIZE,
      offset: nextOffset,
      sport_code: nextSport,
      conference_name: nextConference,
    });

    if (result.data) {
      setListError(null);
      setListHasMore(result.data.pagination.has_more);
      listOffsetRef.current = nextOffset + result.data.groups.length;
      setCheckoutGroups((prev) =>
        append ? [...prev, ...result.data!.groups] : result.data!.groups
      );
    } else if (!append) {
      setCheckoutGroups([]);
      setListHasMore(false);
      if (result.status === 404) {
        setListError(result.error ?? 'Unknown conference');
      } else if (result.status === 400) {
        setListError(result.error ?? 'Invalid filter');
      } else {
        setListError(result.error ?? 'Failed to load checkouts');
      }
    }

    setListLoading(false);
    setListLoadingMore(false);
  }, [appliedListFilter, debouncedListSearch]);

  useEffect(() => {
    void loadCheckoutList({ offset: 0, append: false });
  }, [debouncedListSearch, appliedListFilter]);

  const applyListFilters = () => {
    listOffsetRef.current = 0;
    setAppliedListFilter({
      sport_code: filterSportCode.trim() || undefined,
      conference_name: filterConferenceName.trim() || undefined,
    });
  };

  useEffect(() => {
    if (!listHasMore) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (listLoadingRef.current) return;
        void loadCheckoutList({ append: true });
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [listHasMore, checkoutGroups.length, loadCheckoutList]);

  const clearConferenceFilters = () => {
    setFilterSportCode('');
    setFilterConferenceName('');
    setConferenceOptions([]);
    listOffsetRef.current = 0;
    setCheckoutGroups([]);
    setAppliedListFilter({});
  };

  const isInitialListLoad = listLoading && checkoutGroups.length === 0;

  return (
    <div className="p-4 bg-white shadow-md rounded-md mb-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-800">Direct Checkout</h2>
        <p className="text-gray-600 text-sm mt-1">
          Card payments via Stripe Checkout, grouped by billing user.
        </p>
      </div>

      <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <div className="min-w-0 flex-1 max-w-xl">
          <label htmlFor="checkoutListSearch" className="block text-sm font-medium text-gray-700 mb-1">
            Search checkouts
          </label>
          <div className="relative">
            <div className="flex">
              <input
                type="text"
                id="checkoutListSearch"
                className="w-full p-2 border rounded-l-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Search by name, username, email, or team…"
                value={listSearch}
                onChange={(e) => setListSearch(e.target.value)}
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setListSearch('')}
                className="bg-gray-100 text-gray-700 px-3 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
                aria-label={listSearch ? 'Clear search' : 'Search'}
              >
                {listSearch ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-end justify-end gap-2 shrink-0 xl:ml-4">
          <label className="flex flex-col gap-1 text-xs text-gray-500">
            Sport
            <select
              className="w-40 max-w-full p-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filterSportCode}
              onChange={(e) => setFilterSportCode(e.target.value)}
            >
              <option value="">All sports</option>
              {SPORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-500">
            Conference
            <select
              className="w-48 max-w-full p-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
              value={filterConferenceName}
              disabled={conferencesLoading}
              onChange={(e) => setFilterConferenceName(e.target.value)}
            >
              <option value="">
                {conferencesLoading ? 'Loading…' : 'All conferences'}
              </option>
              {conferenceOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={applyListFilters}
            disabled={listLoading}
            className="px-3 py-2 text-sm border border-transparent rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            Apply
          </button>
          {(filterSportCode ||
            filterConferenceName ||
            appliedListFilter.sport_code ||
            appliedListFilter.conference_name) && (
            <button
              type="button"
              onClick={clearConferenceFilters}
              className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50"
            >
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              listOffsetRef.current = 0;
              void loadCheckoutList({ offset: 0, append: false });
            }}
            disabled={listLoading}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Refresh
          </button>
        </div>
      </div>

      <section>
        <div className="mb-3">
          <h3 className="text-lg font-medium text-gray-900">Checkout payments</h3>
          <p className="text-sm text-gray-500">
            Grouped by billing user · Paid per line · scroll for more
          </p>
        </div>

        {listError && (
          <div className="mb-3 p-3 bg-red-100 border border-red-400 text-red-700 rounded">{listError}</div>
        )}

        {listLoading && checkoutGroups.length > 0 && (
          <div className="mb-2 flex items-center gap-2 text-sm text-gray-600">
            <Spinner size="sm" />
            <span>Updating list…</span>
          </div>
        )}

        {isInitialListLoad ? (
          <div className="flex justify-center items-center h-40">
            <Spinner size="md" />
            <span className="ml-2 text-gray-600">Loading checkouts…</span>
          </div>
        ) : (
          <>
            <CheckoutListTable groups={checkoutGroups} />
            <div ref={loadMoreSentinelRef} className="h-4" aria-hidden />
            {listLoadingMore && (
              <div className="flex justify-center items-center py-4 text-sm text-gray-600">
                <Spinner size="sm" />
                <span className="ml-2">Loading more…</span>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default AdminCheckoutTab;
